/**
 * 市民端「访问人次」统计 · 前后端同一份契约真本（2026-09-21）。
 *
 * ════════ 这件事是怎么来的 ════════
 *
 * 主公 2026-09-21：「统计一下每天的浏览量啥的一些指标，然后在后台呈现出来」，
 * 口径当场收窄为「**就统计一下人次即可，时效性从埋点后开始即可，之前的不用了**」。
 * 所以本契约只服务一件事：**每天有多少次访问、多少个访客**。
 * 漏斗、留存、停留时长、来源渠道一概不做 —— 不是做不了，是没人要。
 *
 * ════════ 为什么不用第三方统计（百度统计 / 友盟 / GA）════════
 *
 * 看一眼市民端的路由就知道不能用：
 *     /my/cases/CD-2026-0198        ← 案件受理号在地址里
 *     /credit/user/<userId>         ← 用户 ID 在地址里
 * 第三方统计脚本上报的是**完整 URL**。装上去等于把「某人打开了某件投诉」
 * 连同 referrer、设备指纹一起送到协会管不着的服务器上。这是投诉平台，
 * 投诉人与被投诉人都是实名的行业内人士 —— 这条路是硬否决，不是口味问题。
 * （附带：GA 在境内打不开，会让市民端卡在脚本超时上。）
 *
 * ════════ 于是路径必须在**离开浏览器之前**就脱敏 ════════
 *
 * 上报的是**路由模板**而不是真实地址：发 `/my/cases/:billNo`，不发
 * `/my/cases/CD-2026-0198`。归一化由 `normalizePageViewPath()` 在**客户端**完成，
 * 案件号与用户 ID 一个字节都不上网、不进库、不进 nginx 日志。
 *
 * ★ 服务端**不信**客户端送来的 path，落库前再比一次白名单（见下一节）。
 *   两端各挡一次，但只有这一份判据 —— 与本目录其余几份契约同一条纪律。
 *
 * ════════ 为什么认不出的路径要归桶而不是丢弃 ════════
 *
 * 白名单必然会漏 —— 以后加一页却忘了往这里登记，是迟早的事
 * （中枢记忆「新增被测物要进每张扫描清单」记的就是这个坑）。
 * 若漏登记＝丢弃，那**人次就少算了**，而人次正是主公唯一要的那个数，
 * 且少算得悄无声息、没有任何告警。
 *
 * 所以：认不出的一律计入 `OTHER_PAGE_VIEW_PATH` 这一桶，**照常计入人次**，
 * 服务端打一条 warn。代价只是「分不清那几次是哪一页」—— 而按页分布本就不是
 * 第一期要呈现的东西。**总数的正确性不依赖白名单的完整性**，这是故意的。
 */

/** 上报接口的地址。前端别再手写字面量，从这里取。 */
export const PAGE_VIEW_ENDPOINT = '/api/citizen/track';

/**
 * 访客标识存在 localStorage 的哪个键下。
 *
 * ★ 存的是**浏览器本地随机生成的 ID**，不是 IP、不是手机号、跟任何人对不上号。
 *   之所以不用 IP：投诉平台留访问者 IP 本身就是个负担，而算 UV 根本不需要它。
 */
export const PAGE_VIEW_VISITOR_KEY = 'cdfjx_vid';

/** 访客 ID 的长度（十六进制字符数）。32 位＝128 bit，碰撞可以不必考虑。 */
export const PAGE_VIEW_VISITOR_LEN = 32;

/** 认不出的路径统一归到这一桶。**照常计入人次**，只是分不出是哪一页。 */
export const OTHER_PAGE_VIEW_PATH = '(其他)';

/**
 * 市民端全部可统计路由 —— 与 `packages/citizen/src/App.tsx` 的 `<Route path>` 对齐。
 *
 * ★ 加了新页别忘了往这里补一条。忘了也不会少算人次（会落进 `(其他)` 桶），
 *   但服务端日志里会出现 warn，体检时看得见。
 */
export const PAGE_VIEW_PATHS: readonly string[] = [
  '/',
  '/notice',
  '/submit',
  '/success',
  '/login',
  '/my',
  '/my/cases',
  '/my/cases/:billNo',
  '/my/cases/:billNo/amend',
  '/my/cases/:billNo/supplement',
  '/my/received',
  '/my/received/:billNo',
  '/my/received/:billNo/defense',
  '/credit',
  '/credit/user/:userId',
  '/credit/black-user',
  '/credit/org',
  '/consult',
  // ★ PC 版（pc.html，协会官网挂链接进来的那一张）。它是**独立入口、没有路由器**，
  //   整个「投诉须知 → 填表 → 提交成功」都在同一张页面里换面板。所以它只在
  //   页面加载时报一次，一次加载＝一人次 —— 与手机端「每切一个路由算一次」
  //   不是同一种数法，但各自都是对自己那种形态的如实计数。
  '/pc',
];

/** 路径段数上限。防止有人拿一条几百段的地址来撑爆归一化。 */
const MAX_PATH_SEGMENTS = 12;

/** 把一条路径拆成段（去掉首尾斜杠与空段）。 */
function segmentsOf(pathname: string): string[] {
  return pathname.split('/').filter((s) => s !== '');
}

/** 预先把白名单拆好，避免每次上报都重拆一遍。 */
const COMPILED: Array<{ template: string; segs: string[] }> = PAGE_VIEW_PATHS.map((template) => ({
  template,
  segs: segmentsOf(template),
}));

/**
 * 真实地址 → 路由模板。认不出返回 `OTHER_PAGE_VIEW_PATH`。
 *
 * ★ **客户端在上报前必须先跑这一趟**，这样案件号与用户 ID 根本不会离开浏览器。
 * ★ 服务端收到后再跑一次校验（`isKnownPageViewPath`），不信客户端送来的值。
 *
 * 只认「段数相同、字面段逐段相等」的模板；`:xxx` 匹配任意一段。
 * 不做正则、不做前缀匹配 —— 这里的输入来自公网，越笨越安全。
 */
export function normalizePageViewPath(pathname: unknown): string {
  if (typeof pathname !== 'string') return OTHER_PAGE_VIEW_PATH;
  // 查询串与哈希一律扔掉：里面可能有回跳地址之类的东西，与人次无关。
  const clean = pathname.split('?')[0]!.split('#')[0]!;
  const segs = segmentsOf(clean);
  if (segs.length > MAX_PATH_SEGMENTS) return OTHER_PAGE_VIEW_PATH;
  if (segs.length === 0) return '/';
  for (const cand of COMPILED) {
    if (cand.segs.length !== segs.length) continue;
    let hit = true;
    for (let i = 0; i < segs.length; i += 1) {
      const want = cand.segs[i]!;
      if (want.startsWith(':')) continue; // 参数段，任意值都算命中
      if (want !== segs[i]) {
        hit = false;
        break;
      }
    }
    if (hit) return cand.template;
  }
  return OTHER_PAGE_VIEW_PATH;
}

/** 这条 path 是不是白名单里的模板（或那个归桶值）。服务端落库前的最后一道闸。 */
export function isKnownPageViewPath(path: unknown): boolean {
  if (typeof path !== 'string') return false;
  return path === OTHER_PAGE_VIEW_PATH || PAGE_VIEW_PATHS.includes(path);
}

/** 访客 ID 是不是合法形状：正好 32 个小写十六进制字符。 */
export function isValidVisitorId(id: unknown): boolean {
  return typeof id === 'string' && new RegExp(`^[0-9a-f]{${PAGE_VIEW_VISITOR_LEN}}$`).test(id);
}

/** 上报体。字段就这三个，别再加 —— 每加一个都得先回答「这是不是个人信息」。 */
export interface PageViewBeacon {
  /** 路由模板，已由 `normalizePageViewPath()` 归一化过 */
  path: string;
  /** 浏览器本地随机 ID，见 `PAGE_VIEW_VISITOR_KEY` */
  visitor: string;
  /** 这一次浏览时是否已登录。只存真假，不存是谁 */
  authed: boolean;
}

/** 后台看板里的一天。 */
export interface PageViewDay {
  /** YYYY-MM-DD（北京时间） */
  day: string;
  /** 当天访问人次（页面打开次数） */
  views: number;
  /** 当天访客数（按访客 ID 去重） */
  visitors: number;
}

/** GET /api/page-views 的整份回执。 */
export interface PageViewStats {
  /** 按天升序，**只含有数据的那些天**（补零由前端按所选区间做） */
  days: PageViewDay[];
  /** 统计起始日 —— 埋点上线那天。在此之前没有数据，也不打算有 */
  since: string | null;
  /** 建站以来累计人次 */
  totalViews: number;
  /** 建站以来累计访客数（全期去重，不是每日访客数之和） */
  totalVisitors: number;
}
